import React, { useCallback, useEffect, useState } from 'react';
import { apiService } from '../../services/api.service';

type CloneAccountBatchStatus = 'pending' | 'done';

interface CloneAccountBatch {
    id: string;
    lineCount: number;
    status: CloneAccountBatchStatus;
    createdBy: string;
    createdAt: string;
    updatedAt: string;
}

interface CloneAccountBatchDetails extends CloneAccountBatch {
    text: string;
}

interface CloneAccountBatchCreated {
    id: string;
    lineCount: number;
    duplicatesRemoved: number;
    status: CloneAccountBatchStatus;
    createdAt: string;
}

interface CloneAccountBatchList {
    data: CloneAccountBatch[];
    total: number;
    hasMore: boolean;
}

const AdminCloneAccountsPage: React.FC = () => {
    const [text, setText] = useState('');
    const [batches, setBatches] = useState<CloneAccountBatch[]>([]);
    const [hasMoreBatches, setHasMoreBatches] = useState(false);
    const [viewedBatch, setViewedBatch] = useState<CloneAccountBatchDetails | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [busyBatchId, setBusyBatchId] = useState<string | null>(null);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    const loadBatches = useCallback(async (offset = 0, append = false) => {
        setIsLoading(true);
        setError('');
        try {
            const result = await apiService.get<CloneAccountBatchList>(`/admin/clone-accounts?offset=${offset}`);
            setBatches((current) => append ? [...current, ...result.data] : result.data);
            setHasMoreBatches(result.hasMore);
        } catch (requestError: any) {
            setError(requestError.response?.data?.message || 'Không thể tải danh sách batch acc clone.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        void loadBatches();
    }, [loadBatches]);

    const saveBatch = async () => {
        if (!text.trim()) {
            setError('Vui lòng nhập danh sách acc clone.');
            setMessage('');
            return;
        }
        setIsSaving(true);
        setError('');
        setMessage('');
        try {
            const result = await apiService.post<CloneAccountBatchCreated>('/admin/clone-accounts', { text });
            setText('');
            setMessage(`Đã lưu ${result.lineCount} acc. Đã bỏ ${result.duplicatesRemoved} dòng trùng trong lần nhập này.`);
            await loadBatches();
        } catch (requestError: any) {
            setError(requestError.response?.data?.message || 'Không thể lưu batch acc clone.');
        } finally {
            setIsSaving(false);
        }
    };

    const viewBatch = async (batch: CloneAccountBatch) => {
        if (viewedBatch?.id === batch.id) {
            setViewedBatch(null);
            return;
        }
        setBusyBatchId(batch.id);
        setError('');
        try {
            setViewedBatch(await apiService.get<CloneAccountBatchDetails>(`/admin/clone-accounts/${batch.id}`));
        } catch (requestError: any) {
            setError(requestError.response?.data?.message || 'Không thể giải mã nội dung batch.');
        } finally {
            setBusyBatchId(null);
        }
    };

    const toggleBatchStatus = async (batch: CloneAccountBatch) => {
        const status: CloneAccountBatchStatus = batch.status === 'pending' ? 'done' : 'pending';
        setBusyBatchId(batch.id);
        setError('');
        setMessage('');
        try {
            const updated = await apiService.patch<CloneAccountBatch>(`/admin/clone-accounts/${batch.id}/status`, { status });
            setBatches((current) => current.map((item) => item.id === batch.id ? updated : item));
            setViewedBatch((current) => current?.id === batch.id ? { ...current, status } : current);
        } catch (requestError: any) {
            setError(requestError.response?.data?.message || 'Không thể cập nhật trạng thái batch.');
        } finally {
            setBusyBatchId(null);
        }
    };

    const deleteBatch = async (batch: CloneAccountBatch) => {
        if (!window.confirm(`Xóa batch gồm ${batch.lineCount} acc? Thao tác này không thể hoàn tác.`)) return;
        setBusyBatchId(batch.id);
        setError('');
        setMessage('');
        try {
            await apiService.delete(`/admin/clone-accounts/${batch.id}`);
            setBatches((current) => current.filter((item) => item.id !== batch.id));
            setViewedBatch((current) => current?.id === batch.id ? null : current);
            setMessage('Đã xóa batch acc clone.');
        } catch (requestError: any) {
            setError(requestError.response?.data?.message || 'Không thể xóa batch acc clone.');
        } finally {
            setBusyBatchId(null);
        }
    };

    const copyViewedBatch = async () => {
        if (!viewedBatch) return;
        setError('');
        try {
            await navigator.clipboard.writeText(viewedBatch.text);
            setMessage('Đã copy nội dung batch.');
        } catch {
            setError('Không thể copy nội dung. Hãy chọn và copy thủ công.');
        }
    };

    return (
        <div className="h-full overflow-y-auto bg-gray-50 p-4 sm:p-6">
            <div className="mx-auto max-w-5xl space-y-5">
                <section className="space-y-4 rounded-2xl bg-white p-5 shadow-sm">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">QL acc clone</h2>
                        <p className="mt-1 text-sm text-gray-600">
                            Mỗi batch tối đa 5.000 dòng theo định dạng username|password. Dòng trùng username chỉ bị bỏ trong cùng một lần nhập.
                        </p>
                    </div>
                    <label className="block">
                        <span className="mb-1 block text-sm font-medium text-gray-700">Danh sách tài khoản</span>
                        <textarea
                            value={text}
                            onChange={(event) => setText(event.target.value)}
                            maxLength={5 * 1024 * 1024}
                            rows={12}
                            spellCheck={false}
                            placeholder={'nopw15242|password1\nnopw15243|password2'}
                            className="w-full resize-y rounded-xl border border-gray-300 px-4 py-3 font-mono text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        />
                        <span className="mt-1 block text-right text-xs text-gray-500">
                            {text.split(/\r?\n/).filter((line) => line.trim()).length.toLocaleString()} / 5.000 dòng
                        </span>
                    </label>
                    {(error || message) && (
                        <div role={error ? 'alert' : 'status'} className={`rounded-xl px-4 py-3 text-sm ${error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                            {error || message}
                        </div>
                    )}
                    <button
                        type="button"
                        onClick={() => void saveBatch()}
                        disabled={isSaving || !text.trim()}
                        className="rounded-xl bg-blue-600 px-5 py-3 font-semibold text-white transition hover:bg-blue-700 disabled:cursor-wait disabled:opacity-50"
                    >
                        {isSaving ? 'Đang mã hóa và lưu...' : 'Lưu batch'}
                    </button>
                </section>

                <section className="space-y-4 rounded-2xl bg-white p-5 shadow-sm">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Các batch đã lưu</h3>
                            <p className="mt-1 text-sm text-gray-500">Mỗi batch có một trạng thái chung.</p>
                        </div>
                        <button type="button" onClick={() => void loadBatches()} disabled={isLoading} className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                            Làm mới
                        </button>
                    </div>
                    {isLoading ? (
                        <p className="py-6 text-center text-sm text-gray-500">Đang tải...</p>
                    ) : batches.length === 0 ? (
                        <p className="py-6 text-center text-sm text-gray-500">Chưa có batch nào.</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-left text-sm">
                                <thead className="bg-gray-50 text-gray-600">
                                    <tr>
                                        <th className="px-3 py-2">Ngày nhập</th>
                                        <th className="px-3 py-2">Số acc</th>
                                        <th className="px-3 py-2">Trạng thái</th>
                                        <th className="px-3 py-2">Người nhập</th>
                                        <th className="px-3 py-2">Thao tác</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {batches.map((batch) => (
                                        <tr key={batch.id}>
                                            <td className="whitespace-nowrap px-3 py-3">{new Date(batch.createdAt).toLocaleString()}</td>
                                            <td className="px-3 py-3">{batch.lineCount.toLocaleString()}</td>
                                            <td className="px-3 py-3">
                                                <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${batch.status === 'pending' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`}>
                                                    {batch.status === 'pending' ? 'Pending' : 'Done'}
                                                </span>
                                            </td>
                                            <td className="px-3 py-3">{batch.createdBy}</td>
                                            <td className="px-3 py-3">
                                                <div className="flex flex-wrap gap-2">
                                                    <button type="button" onClick={() => void viewBatch(batch)} disabled={busyBatchId === batch.id} className="rounded-lg border border-blue-200 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50">
                                                        {busyBatchId === batch.id ? 'Đang xử lý...' : viewedBatch?.id === batch.id ? 'Ẩn nội dung' : 'Xem nội dung'}
                                                    </button>
                                                    <button type="button" onClick={() => void toggleBatchStatus(batch)} disabled={busyBatchId === batch.id} className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                                                        Đổi thành {batch.status === 'pending' ? 'Done' : 'Pending'}
                                                    </button>
                                                    <button type="button" onClick={() => void deleteBatch(batch)} disabled={busyBatchId === batch.id} className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50">
                                                        Xóa
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {!isLoading && hasMoreBatches && (
                        <button
                            type="button"
                            onClick={() => void loadBatches(batches.length, true)}
                            className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                        >
                            Tải thêm batch
                        </button>
                    )}
                </section>

                {viewedBatch && (
                    <section className="space-y-3 rounded-2xl bg-white p-5 shadow-sm">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <h3 className="font-semibold text-gray-900">Nội dung batch · {viewedBatch.lineCount.toLocaleString()} acc</h3>
                            <button type="button" onClick={() => void copyViewedBatch()} className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                                Copy nội dung
                            </button>
                        </div>
                        <textarea
                            readOnly
                            value={viewedBatch.text}
                            rows={12}
                            spellCheck={false}
                            className="w-full resize-y rounded-xl border border-gray-300 px-4 py-3 font-mono text-sm outline-none"
                        />
                    </section>
                )}
            </div>
        </div>
    );
};

export default AdminCloneAccountsPage;
